import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import * as api from "../api/PromptLibraryApi";
import type { PromptCategory, Prompt, PromptCreate, PromptUpdate, CategoryCreate, CategoryUpdate, PromptFilters } from "../api/PromptLibraryApi";
import type { ColorScheme } from "../ui/placeholders";
import { VerifyContext } from "../../../app/VerifyContext";

interface PromptLibraryContextValue {
  categories: PromptCategory[];
  prompts: Prompt[];
  categoriesLoading: boolean;
  promptsLoading: boolean;
  error: string;
  filters: PromptFilters;
  activeCategoryId: number | null;
  colorScheme: ColorScheme | null;

  setActiveCategoryId: (id: number | null) => void;
  setFilters: (f: PromptFilters) => void;
  loadCategories: () => Promise<void>;
  loadPrompts: (f?: PromptFilters) => Promise<void>;

  createCategory: (data: CategoryCreate) => Promise<void>;
  updateCategory: (id: number, data: CategoryUpdate) => Promise<void>;
  deleteCategory: (id: number) => Promise<void>;

  createPrompt: (data: PromptCreate) => Promise<void>;
  updatePrompt: (id: number, data: PromptUpdate) => Promise<void>;
  deletePrompt: (id: number) => Promise<void>;
}

const PromptLibraryContext = createContext<PromptLibraryContextValue | null>(null);

export function PromptLibraryProvider({ children }: { children: React.ReactNode }) {
  const { authUserType } = useContext(VerifyContext);
  const [categories, setCategories] = useState<PromptCategory[]>([]);
  const [prompts, setPrompts] = useState<Prompt[]>([]);
  const [categoriesLoading, setCategoriesLoading] = useState(false);
  const [promptsLoading, setPromptsLoading] = useState(false);
  const [error, setError] = useState("");
  const [activeCategoryId, setActiveCategoryId] = useState<number | null>(null);
  const [filters, setFiltersState] = useState<PromptFilters>({});
  const [colorScheme, setColorScheme] = useState<ColorScheme | null>(null);

  useEffect(() => {
    api.getMyColorScheme().then(setColorScheme).catch(() => setColorScheme(null));
  }, [authUserType]);

  const setFilters = useCallback((f: PromptFilters) => {
    setFiltersState(f);
  }, []);

  const loadCategories = useCallback(async () => {
    setCategoriesLoading(true);
    try {
      const data = await api.listCategories();
      setCategories(data);
    } catch {
      setError("Failed to load categories");
    } finally {
      setCategoriesLoading(false);
    }
  }, []);

  const loadPrompts = useCallback(async (f?: PromptFilters) => {
    setPromptsLoading(true);
    try {
      const data = await api.listPrompts(f ?? filters);
      setPrompts(data);
    } catch {
      setError("Failed to load prompts");
    } finally {
      setPromptsLoading(false);
    }
  }, [filters]);

  useEffect(() => { loadCategories(); }, [loadCategories]);
  useEffect(() => {
    const f: PromptFilters = { ...filters };
    if (activeCategoryId !== null) f.category_id = activeCategoryId;
    else delete f.category_id;
    loadPrompts(f);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeCategoryId, filters.search]);

  const createCategory = useCallback(async (data: CategoryCreate) => {
    await api.createCategory(data);
    await loadCategories();
  }, [loadCategories]);

  const updateCategory = useCallback(async (id: number, data: CategoryUpdate) => {
    await api.updateCategory(id, data);
    await loadCategories();
  }, [loadCategories]);

  const deleteCategory = useCallback(async (id: number) => {
    await api.deleteCategory(id);
    if (activeCategoryId === id) setActiveCategoryId(null);
    await loadCategories();
  }, [loadCategories, activeCategoryId]);

  const createPrompt = useCallback(async (data: PromptCreate) => {
    await api.createPrompt(data);
    const f: PromptFilters = { ...filters };
    if (activeCategoryId !== null) f.category_id = activeCategoryId;
    await loadPrompts(f);
    await loadCategories();
  }, [filters, activeCategoryId, loadPrompts, loadCategories]);

  const updatePrompt = useCallback(async (id: number, data: PromptUpdate) => {
    await api.updatePrompt(id, data);
    const f: PromptFilters = { ...filters };
    if (activeCategoryId !== null) f.category_id = activeCategoryId;
    await loadPrompts(f);
    await loadCategories();
  }, [filters, activeCategoryId, loadPrompts, loadCategories]);

  const deletePrompt = useCallback(async (id: number) => {
    await api.deletePrompt(id);
    const f: PromptFilters = { ...filters };
    if (activeCategoryId !== null) f.category_id = activeCategoryId;
    await loadPrompts(f);
    await loadCategories();
  }, [filters, activeCategoryId, loadPrompts, loadCategories]);

  const value = useMemo<PromptLibraryContextValue>(() => ({
    categories, prompts, categoriesLoading, promptsLoading, error,
    filters, activeCategoryId, colorScheme,
    setActiveCategoryId, setFilters, loadCategories, loadPrompts,
    createCategory, updateCategory, deleteCategory,
    createPrompt, updatePrompt, deletePrompt,
  }), [
    categories, prompts, categoriesLoading, promptsLoading, error,
    filters, activeCategoryId, colorScheme,
    setActiveCategoryId, setFilters, loadCategories, loadPrompts,
    createCategory, updateCategory, deleteCategory,
    createPrompt, updatePrompt, deletePrompt,
  ]);

  return <PromptLibraryContext.Provider value={value}>{children}</PromptLibraryContext.Provider>;
}

export function usePromptLibrary(): PromptLibraryContextValue {
  const ctx = useContext(PromptLibraryContext);
  if (!ctx) throw new Error("usePromptLibrary must be used within PromptLibraryProvider");
  return ctx;
}
